import { useEffect, useRef } from "react";
var isRecord = function (val) {
    return val !== null && typeof val === "object";
};
var hasMessage = function (val) {
    return isRecord(val) && "message" in val;
};
var getFirstErrorPath = function (obj, path) {
    if (path === void 0) { path = ""; }
    for (var _i = 0, _a = Object.keys(obj); _i < _a.length; _i++) {
        var key = _a[_i];
        var value = obj[key];
        var currentPath = path ? "".concat(path, ".").concat(key) : key;
        if (hasMessage(value)) {
            return currentPath;
        }
        if (isRecord(value)) {
            var nestedPath = getFirstErrorPath(value, currentPath);
            if (nestedPath)
                return nestedPath;
        }
    }
    return null;
};
/**
 * Collects EVERY invalid field (path + message) from a react-hook-form error
 * tree. Used to build a human-readable summary when a field cannot be focused
 * in the DOM, so the user always sees which fields need attention.
 */
var collectInvalidFields = function (obj, path, acc) {
    if (path === void 0) { path = ""; }
    if (acc === void 0) { acc = []; }
    for (var _i = 0, _a = Object.keys(obj); _i < _a.length; _i++) {
        var key = _a[_i];
        var value = obj[key];
        var currentPath = path ? "".concat(path, ".").concat(key) : key;
        if (hasMessage(value)) {
            acc.push({
                path: currentPath,
                message: typeof value.message === "string" ? value.message : currentPath,
            });
        }
        else if (isRecord(value)) {
            collectInvalidFields(value, currentPath, acc);
        }
    }
    return acc;
};
var MAX_FOCUS_ATTEMPTS = 5;
/**
 * Focuses the element and reports whether it really took the focus. A disabled,
 * hidden or otherwise non-focusable element silently ignores `focus()`, so
 * merely finding it in the DOM does not mean the user was taken to the field.
 */
var focusElement = function (element) {
    element.focus({ preventScroll: true });
    if (document.activeElement !== element)
        return false;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
};
/**
 * Scrolls to and focuses the first invalid form field. When the field cannot be
 * focused — it is not in the DOM (collapsed accordion, value object without a
 * matching input) or it is disabled, hidden or not focusable — it first asks
 * the caller to reveal the section and retries across a few animation frames;
 * if the field still cannot take the focus it hands the complete list of
 * invalid fields to `onUnresolvedErrors` so the submit never fails silently.
 *
 * @param errors react-hook-form error tree.
 * @param handlers optional recovery callbacks (section reveal + fallback).
 */
export var scrollToError = function (errors, handlers) {
    var _a, _b;
    var firstErrorField = getFirstErrorPath(errors);
    if (!firstErrorField)
        return;
    (_a = handlers === null || handlers === void 0 ? void 0 : handlers.openSectionForField) === null || _a === void 0 ? void 0 : _a.call(handlers, firstErrorField);
    var focusField = function () {
        return Array.from(document.querySelectorAll("[name=\"".concat(firstErrorField, "\"]"))).some(function (candidate) {
            return candidate instanceof HTMLElement && focusElement(candidate);
        });
    };
    if (focusField())
        return;
    // The field is not focusable yet (freshly expanded or unlocked section) or
    // has no focusable input at all. Retry across a few frames before falling back.
    if (typeof requestAnimationFrame !== "function") {
        (_b = handlers === null || handlers === void 0 ? void 0 : handlers.onUnresolvedErrors) === null || _b === void 0 ? void 0 : _b.call(handlers, collectInvalidFields(errors));
        return;
    }
    var attempts = 0;
    var retry = function () {
        var _a;
        attempts += 1;
        if (focusField())
            return;
        if (attempts < MAX_FOCUS_ATTEMPTS) {
            requestAnimationFrame(retry);
            return;
        }
        (_a = handlers === null || handlers === void 0 ? void 0 : handlers.onUnresolvedErrors) === null || _a === void 0 ? void 0 : _a.call(handlers, collectInvalidFields(errors));
    };
    requestAnimationFrame(retry);
};
export var useScrollOnPotentialDuplicates = function (potentialDuplicates) {
    var topRef = useRef(null);
    useEffect(function () {
        if (potentialDuplicates.length > 0 && (topRef === null || topRef === void 0 ? void 0 : topRef.current)) {
            requestAnimationFrame(function () {
                var _a;
                (_a = topRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: "smooth" });
            });
        }
    }, [potentialDuplicates, topRef]);
    return { topRef: topRef };
};
